import { ExcelExportDto, PowerpointExportDto } from '@ecosystem/export-api';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTrackChartTooltipEvent } from '../../analytics/trackChartInteractions.hook';
import { mapDataVizForChartType, trackPageElementClicked } from '../../analytics/utils';
import { EmptyState } from '../../common/no-data-warning/empty-state';
import { DataVizToggle } from '../../components/dataviz.toggle';
import { Instructions } from '../../content/instructions';
import { DataContentProps } from '../../content/types';
import { useMobile } from '../../context/mobile/mobile.provider';
import { ChartTitle } from '../../design/charts/chartTitle';
import { Loading } from '../../design/loading/Loading';
import GenericWarnings from '../../design/warning/GenericWarnings';
import { chartToPptData } from '../../export';
import { ExportButton, ExportButtonProps } from '../../export/export.button';
import { ExportHideout } from '../../export/ExportHideout';
import { useCorePageState } from '../../page/provider';
import { ComponentHeaderActionType } from '../../page/types';
import { splitIntoChunks } from '../../tools/arrays';
import {
  CollectionOptions,
  DataColumn,
  DataVizConfig,
  DataVizName,
  DataVizType,
  EventElementCategory,
  ExportFormat,
  ExportOptions,
} from '../../types';
import { useOnCollectionExportReady } from '../collection/useOnCollectionExportReady';
import { DISABLED_CHART_MESSAGE } from '../constants';
import { useMobileChart } from '../mobileChart/mobileChart.hook';
import { MobileOverflowMenu } from '../mobileMenu/mobileOverflowMenu';
import { MultiKpiCollectionButton } from '../multiKpi/MultiKpiCollectionButton';
import { MultiKpiCollectionButtonProps } from '../multiKpi/types';
import { createChartRef, getDisplayAs, getChartSliceSize, shouldDisableChart } from '../utils';
import { chartDataToExportData } from './chartDataToExportData';
import { makeSegmentedChartData, SegmentedChartData } from './data';
import { SegmentationChart } from './segmentation.chart';
import { SegmentationTable } from './segmentation.table';

function Segmentation({
  data,
  instructions = {},
  isLoading,
  componentSettings,
  ComponentActionsContainer,
  collection,
  paramsForData,
  paramsForContext,
  updateSettings,
  trackEvent,
  componentMetadata,
  componentHeadingTitle,
  proposition,
}: DataContentProps) {
  const [dataStamp, setDataStamp] = useState<number>();
  const [chartData, setChartData] = useState<SegmentedChartData>();
  const [yColumn, setYColumn] = useState<DataColumn>();
  const [xColumn, setXColumn] = useState<DataColumn>();
  const [currency, setCurrency] = useState<string>();
  const [exportRefLength, setExportRefLength] = useState<number>(0);
  const [isChartDisabled, setIsChartDisabled] = useState<boolean>(false);
  const [displayAs, setDisplayAs] = useState<DataVizType>(getDisplayAs(componentSettings));

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const chartSliceRefs = useRef<HTMLDivElement[]>([]);
  const { dsl, page, context, data: dataInfo, collection: collectionInfo, globalWarnings } = useCorePageState();

  const { isMobile } = useMobile();
  const { chartFullscreen, setChartFullscreen, MobileChartButton } = useMobileChart();

  const allColumns = data?.getAllColumns() ?? [];

  const sharedBrands = dsl?.settings?.values?.sharedBrands || [];

  useOnCollectionExportReady({
    collection,
    dsl,
    chartSliceRefs,
    componentSettings,
    allColumns,
    componentHeadingTitle,
    refLength: exportRefLength,
  });

  const myInstructions = new Instructions(instructions, componentSettings);

  const yColumnName = myInstructions.getString('yColumn');
  const xColumnName = myInstructions.getResolvedFieldName('xColumn', 'primary');
  const segmentName = myInstructions.getResolvedFieldName('segmentColumn', 'secondary');
  const exportOptions = myInstructions.getObject('export') as ExportOptions;
  const collectionOptions = myInstructions.getObject('collection') as CollectionOptions;

  const { trackTooltipEvent } = useTrackChartTooltipEvent(
    componentSettings,
    !!collection,
    collectionOptions?.chartName
  );

  const updateData = () => {
    if (!data) {
      return;
    }
    const yColumn = data.getDataColumns().find(col => col.name === yColumnName);
    const xColumn = data.getDataColumns().find(col => col.name === xColumnName);
    const chartData = makeSegmentedChartData(data, paramsForData, {
      yColumnName: yColumnName,
      xColumnName,
      segmentName,
      sharedBrands,
    });

    setCurrency(data.getCurrency());
    setYColumn(yColumn);
    setXColumn(xColumn);
    setChartData(chartData);
    setDataStamp(data.stamp);

    if (chartData) {
      const isDisabled = shouldDisableChart(chartData?.segmentDataSets[0].dataSets);
      setIsChartDisabled(isDisabled);
      if (isDisabled) {
        setDisplayAs(DataVizType.table);
      }
    }
  };

  const handleDataVizChange = useCallback(
    (value: DataVizType) => {
      setDisplayAs(value);
      updateSettings({
        ...componentSettings,
        dataViz: [value],
      });
      trackPageElementClicked(trackEvent, mapDataVizForChartType(value), {
        elementCategory: mapDataVizForChartType(value) as EventElementCategory,
      });
    },
    [componentSettings, updateSettings, trackEvent]
  );

  if (!componentSettings.dataViz?.length) {
    updateSettings({
      ...componentSettings,
      dataViz: [displayAs],
    });
  }

  const sliceChartData = (data: SegmentedChartData, sliceSize: number): SegmentedChartData[] => {
    const [totals, ...segmentDataSets] = data.segmentDataSets;

    return splitIntoChunks(segmentDataSets, sliceSize).map(chunk => ({
      ...chartData,
      segmentDataSets: [totals, ...chunk],
    }));
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!data || data.stamp === dataStamp) {
      return;
    }
    updateData();
  }, [data]);

  useEffect(() => {
    if (data) {
      collection?.onLoaded();
    }
  }, [data]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    updateData();
  }, [yColumnName]);

  useEffect(() => {
    if (!exportModalOpen) {
      chartSliceRefs.current = [];
    }
  }, [exportModalOpen]);

  if (!data) {
    return null;
  }

  if (
    collection &&
    !(chartData && chartData !== undefined && chartData.segmentDataSets.find(sDataSet => sDataSet.dataSets.length > 0))
  ) {
    collection.onExportReady(null);
    return <EmptyState />;
  }

  const getExportData = async (exportFormat: ExportFormat): Promise<PowerpointExportDto | ExcelExportDto> => {
    switch (exportFormat) {
      case ExportFormat.Xlsx:
        return chartDataToExportData(
          exportFormat,
          exportOptions,
          chartData,
          xColumn,
          yColumn,
          currency,
          globalWarnings
        );

      case ExportFormat.Pptx:
        return await chartToPptData(
          exportOptions,
          dsl,
          chartData.yLabel,
          chartSliceRefs.current,
          componentSettings,
          allColumns,
          componentHeadingTitle
        );

      default:
        break;
    }
  };

  const dataVizConfig: DataVizConfig[] = [
    {
      type: DataVizType.chart,
      isDisabled: isChartDisabled,
      tooltipContent: DISABLED_CHART_MESSAGE,
      name: DataVizName.chart,
    },
    {
      type: DataVizType.table,
      isDisabled: false,
      name: DataVizName.table,
    },
  ];

  const multiKpiCollectionButtonProps: MultiKpiCollectionButtonProps = {
    collectionOptions,
    collectionInfo,
    proposition,
    paramsForData,
    paramsForContext,
    context,
    dataInfo,
    page,
    componentSettings,
    isDisabled: isLoading,
  };

  const exportButtonProps: ExportButtonProps = {
    trackingSourceData: data,
    componentSettings,
    componentMetadata,
    disabled: isLoading,
    exportFormats: [ExportFormat.Xlsx, ExportFormat.Pptx],
    getExportData,
    exportContentRef: chartSliceRefs,
    onModalVisibilityChange: isVisible => setExportModalOpen(isVisible),
    trackEvent,
  };

  const renderActionsContainer = () => {
    return (
      <>
        <MultiKpiCollectionButton {...multiKpiCollectionButtonProps} actionType={ComponentHeaderActionType.Button} />
        {exportOptions && <ExportButton {...exportButtonProps} actionType={ComponentHeaderActionType.Button} />}
        <DataVizToggle options={dataVizConfig} value={displayAs} onChange={handleDataVizChange} />
      </>
    );
  };

  const renderMobileActionsContainer = () => {
    return (
      <MobileOverflowMenu
        isLoading={isLoading}
        isAiEnabled={false}
        dataOptions={dataVizConfig}
        canExportDataSet={!!exportOptions}
        canEditKpis={false}
        canToggleDataViz={true}
        handleDataVizChange={handleDataVizChange}
        selectedDataViz={displayAs}
        multiKpiCollectionButtonProps={multiKpiCollectionButtonProps}
        exportButtonProps={exportButtonProps}
      />
    );
  };
  const chartSliceSize = getChartSliceSize(chartData?.segmentDataSets?.length, chartData?.dataLabels?.length); // Number of charts per export slide

  return (
    <>
      {chartData && ComponentActionsContainer && (
        <ComponentActionsContainer>
          {!isMobile ? renderActionsContainer() : renderMobileActionsContainer()}
        </ComponentActionsContainer>
      )}
      <div className={classNames({ 'chart-full-screen': chartFullscreen })}>
        {isMobile && !isLoading && (
          <MobileChartButton chartFullscreen={chartFullscreen} setChartFullscreen={setChartFullscreen} />
        )}
        <div
          className={classNames('segmentation relative w-full max-w-full', {
            'pt-4': !chartFullscreen || displayAs === 'table',
          })}
        >
          <Loading isActive={isLoading} withIndicator />
          {displayAs === 'table' && chartData && (
            <>
              <SegmentationTable data={chartData} xColumn={xColumn} chartFullscreen={chartFullscreen} />
              {(exportModalOpen || collection) && (
                <>
                  {sliceChartData(chartData, 5).map((chartDataSlice, idx, arr) => (
                    <ExportHideout key={idx}>
                      <div ref={createChartRef(idx, arr.length, setExportRefLength, chartSliceRefs)}>
                        {globalWarnings && <GenericWarnings warnings={globalWarnings} />}
                        <SegmentationTable data={chartDataSlice} xColumn={xColumn} chartFullscreen={chartFullscreen} />
                      </div>
                    </ExportHideout>
                  ))}
                </>
              )}
            </>
          )}
          {displayAs === 'chart' && chartData && (
            <>
              <ChartTitle title={chartData?.yLabel} />
              <SegmentationChart
                yColumn={yColumn}
                chartData={chartData}
                currency={currency}
                trackTooltipEvent={trackTooltipEvent}
                chartFullscreen={chartFullscreen}
              />
              {(exportModalOpen || collection) && (
                <>
                  {sliceChartData(chartData, chartSliceSize).map((chartDataSlice, idx, arr) => (
                    <ExportHideout key={idx}>
                      <div ref={createChartRef(idx, arr.length, setExportRefLength, chartSliceRefs)}>
                        {globalWarnings && <GenericWarnings warnings={globalWarnings} />}
                        <SegmentationChart
                          ref={element => (chartSliceRefs.current[idx] = element)}
                          yColumn={yColumn}
                          chartData={chartDataSlice}
                          currency={currency}
                          trackTooltipEvent={trackTooltipEvent}
                          chartFullscreen={chartFullscreen}
                        />
                      </div>
                    </ExportHideout>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export { Segmentation };
